import { GlassCard } from "@/components/mnf/UI";
import BadgeTierChip from "@/components/mnf/BadgeTierChip";
import {
  queries,
  useStoreQuery,
  type DisciplineState,
  type BadgeTierMeta,
} from "@/lib/store";

/**
 * DisciplineBadgesCard
 *
 * Shows the four spiritual disciplines a sister tracks (meals, verse, prayer,
 * sabbath) and the tier she has earned for each: Beginning, Planted (7d),
 * Rooted (21d), or Bearing Fruit (90d). Her top tier is celebrated in the
 * eyebrow and pairs with that tier's scripture.
 *
 * Grace, not pressure: every discipline always shows progress to the next
 * tier in days, never a percent bar that implies failure. A sister who has
 * not yet planted any discipline still sees "Beginning" in soft forest tone,
 * with a gentle nudge to show up tomorrow.
 */
export default function DisciplineBadgesCard({
  userId,
  variant = "today",
}: {
  userId: string;
  variant?: "today" | "full";
}) {
  const states = useStoreQuery(() => queries.disciplinesForUser(userId));
  const top = useStoreQuery(() => queries.topDisciplineTier(userId));

  if (!states || states.length === 0 || !top) return null;

  return (
    <GlassCard padding={18} className="mnf-fade-up" data-testid="card-disciplines">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
            Spiritual disciplines
          </p>
          <p
            className="mnf-serif mt-1"
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: "var(--mnf-fd)",
              lineHeight: 1.25,
            }}
          >
            {headlineForTier(top)}
          </p>
        </div>
        <BadgeTierChip tier={top} size="md" />
      </div>

      <div
        className="mt-3 p-3"
        style={{
          background: "rgba(30,74,53,0.04)",
          border: "1px solid rgba(30,74,53,0.06)",
          borderRadius: 14,
        }}
        data-testid="block-tier-scripture"
      >
        <p
          className="mnf-serif italic"
          style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}
        >
          &ldquo;{top.scripture}&rdquo;
        </p>
        <p
          className="mnf-body mt-1"
          style={{ fontSize: 11, color: "var(--mnf-tg)", letterSpacing: "0.04em" }}
        >
          {top.scriptureRef}
        </p>
      </div>

      <div className="mt-3 space-y-2">
        {states.map((s) => (
          <DisciplineRow key={s.kind} state={s} variant={variant} />
        ))}
      </div>

      <p
        className="mnf-body mt-3"
        style={{
          fontSize: 11,
          color: "var(--mnf-tg)",
          lineHeight: 1.45,
          fontStyle: "italic",
        }}
      >
        Grace, not pressure. One faithful practice is enough to bear fruit.
      </p>
    </GlassCard>
  );
}

function DisciplineRow({
  state,
  variant,
}: {
  state: DisciplineState;
  variant: "today" | "full";
}) {
  const isAtTop = state.daysToNextTier === null;
  const subline = isAtTop
    ? `${state.currentStreak} days, bearing fruit`
    : state.currentStreak === 0
    ? `Begin today, ${state.tierMeta.threshold === 0 ? 7 : state.daysToNextTier} days to ${state.nextTierLabel}`
    : `${state.currentStreak} day${state.currentStreak === 1 ? "" : "s"}, ${state.daysToNextTier} to ${state.nextTierLabel}`;

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5"
      style={{
        background: "rgba(255,255,255,0.55)",
        border: "1px solid rgba(30,74,53,0.06)",
        borderRadius: 14,
      }}
      data-testid={`row-discipline-${state.kind}`}
    >
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: state.practicedToday
            ? "rgba(192,120,74,0.14)"
            : "rgba(30,74,53,0.06)",
          fontSize: 18,
          border: state.practicedToday
            ? "1px solid rgba(192,120,74,0.32)"
            : "1px solid transparent",
        }}
        aria-hidden
      >
        {state.meta.emoji}
      </span>
      <div className="flex-1 min-w-0">
        <p
          className="mnf-body"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "var(--mnf-fd)",
            lineHeight: 1.3,
          }}
        >
          {variant === "today" ? state.meta.shortLabel : state.meta.label}
        </p>
        <p
          className="mnf-body"
          style={{
            fontSize: 11.5,
            color: "var(--mnf-tg)",
            lineHeight: 1.4,
            marginTop: 2,
          }}
        >
          {subline}
        </p>
      </div>
      <BadgeTierChip tier={state.tierMeta} size="sm" />
    </div>
  );
}

function headlineForTier(tier: BadgeTierMeta): string {
  switch (tier.key) {
    case "bearing-fruit":
      return "You are bearing fruit, sister";
    case "rooted":
      return "Your roots are reaching down";
    case "planted":
      return "Your seed is in the soil";
    case "none":
    default:
      return "Every faithful practice begins here";
  }
}
